import type { Currencies, ReviewTicker } from "@eu.mdotm.api/sphere";
import { unreachable } from "@mdotm/utils/runtime";
import type { TFunction } from "i18next";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SizingEdit } from "$root/functional-areas/instruments-editor/quantity/position-sizing/sizing";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { platformToast } from "$root/notification-system/toast";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { serverTodayAsLocalDate } from "$root/utils";
import { formatCurrencyAmount } from "$root/utils/currency";
import { computeCashMarketValue } from "$root/functional-areas/instruments-editor/quantity/derive";
import { pinnedCashRowGridProps } from "$root/functional-areas/instruments-editor/quantity/pinnedCashRow";
import { ReviewHeader, type ReviewHeaderProps } from "../ReviewHeader";
import { ReviewInstrumentsTable } from "../ReviewInstrumentsTable";
import { type AliasOutcome, type AliasSizingContext, applyAliasOutcome, beginUnrecognizedFix } from "./newAlias";
import { withHeldByValueCells } from "./heldByValueCells";
import { applyReviewChanges, type QuantityReviewRow, rebalanceReviewCash, setDeleted } from "./rows";
import { convertQuantityReviewRows } from "./serialize";
import { type ReviewSaveCheck, reviewSaveCheck } from "./submitChecks";
import { setSyntheticMarketValue, withSyntheticMarketValue } from "./synthetic";

export type SizeAlias = (params: {
	newAlias: string;
	edit: SizingEdit;
	file: QuantityReviewRow;
	context: AliasSizingContext;
}) => Promise<AliasOutcome>;

export type QuantityReviewProps = {
	rows: QuantityReviewRow[];
	aum: number;
	currency: Currencies | undefined;
	canEditComposition: boolean;
	header: Omit<ReviewHeaderProps, "save">;
	onSave(composition: ReviewTicker[]): Promise<void>;
	onDirty(): void;
	sizeAlias: SizeAlias;
};

type ResolvedIssue = { id: string } & Pick<QuantityReviewRow, "newAlias" | "proxies" | "hasBeenReviewed">;

/**
 * The review of a portfolio held in quantities. The only value edited here is a synthetic row's market
 * value; cash follows it, and every delete and restore.
 */
export function QuantityReview({
	rows: loadedRows,
	aum,
	currency,
	canEditComposition,
	header,
	onSave,
	onDirty,
	sizeAlias,
}: QuantityReviewProps): React.JSX.Element {
	const { t } = useTranslation();
	const [rows, setRows] = useState(loadedRows);
	const [loaded, setLoaded] = useState(loadedRows);
	if (loaded !== loadedRows) {
		setLoaded(loadedRows);
		setRows(loadedRows);
	}
	const rowsRef = useRef(rows);
	rowsRef.current = rows;
	const attempts = useRef(0);

	const reviewColumns = columnsDefinition.useInstrumentQuantityReviewComposition(canEditComposition ? "edit" : "read", {
		currency,
	});
	const persistColProps = agExtra.usePersistColState(
		persistedColStateKeys["tableV3-investment-quantityComposition-review"],
	);

	const update = (next: (latest: QuantityReviewRow[]) => QuantityReviewRow[]) => {
		setRows(next);
		onDirty();
	};
	const updateRef = useRef(update);
	updateRef.current = update;

	const columns = useMemo(() => {
		const withCells = withHeldByValueCells(reviewColumns);
		return canEditComposition
			? withSyntheticMarketValue(withCells, (id, marketValue) =>
					updateRef.current((latest) => setSyntheticMarketValue(latest, id, marketValue, aum)),
				)
			: withCells;
	}, [reviewColumns, canEditComposition, aum]);

	const resolveIssues = (issues: ResolvedIssue[]) => {
		const byId = new Map(issues.map((issue) => [issue.id, issue]));
		const now = new Date().toISOString();
		const context: AliasSizingContext = { aum, currency, date: serverTodayAsLocalDate() };
		const started = rowsRef.current.map((row) => {
			const issue = byId.get(row.id);
			if (!issue) {
				return row;
			}
			const resolved = {
				...row,
				proxies: issue.proxies,
				hasBeenReviewed: issue.hasBeenReviewed,
				newAlias: issue.newAlias,
			};
			if (row.issue !== "UNRECOGNIZED_INSTRUMENT") {
				return resolved;
			}
			attempts.current += 1;
			const file = loaded.find((x) => x.id === row.id) ?? row;
			return beginUnrecognizedFix(resolved, file, { attempt: attempts.current, now, aum });
		});
		update(() => rebalanceReviewCash(started, aum));

		for (const row of started) {
			const sizing = row.positionSizing;
			if (!byId.has(row.id) || !row.newAlias || sizing?.status !== "sizing") {
				continue;
			}
			const file = loaded.find((x) => x.id === row.id) ?? row;
			const newAlias = row.newAlias;
			void sizeAlias({ newAlias, edit: sizing.edit, file, context }).then((outcome) => {
				setRows((latest) =>
					applyAliasOutcome({ rows: latest, id: row.id, attempt: sizing.attempt, outcome, context, now }),
				);
				const message = aliasOutcomeMessage(t, newAlias, outcome);
				if (message) {
					platformToast({ children: message, icon: "Portfolio", severity: "warning" });
				}
			});
		}
	};

	const save = async () => {
		const check = reviewSaveCheck(rows, aum);
		if (check.kind !== "ok") {
			platformToast({
				children: blockedSaveMessage(t, check, rows, aum, currency),
				icon: "Portfolio",
				severity: "warning",
			});
			return;
		}
		await onSave(convertQuantityReviewRows(rows));
	};

	return (
		<>
			<ReviewHeader {...header} save={canEditComposition ? { kind: "ready", onSave: save } : undefined} />
			<ReviewInstrumentsTable<QuantityReviewRow>
				{...persistColProps}
				{...agExtra.aggFunctions()}
				{...pinnedCashRowGridProps<QuantityReviewRow>()}
				grandTotalRow="pinnedBottom"
				rows={rows}
				keepRow={isCashRow}
				getContextMenuItems={(params) => (params.defaultItems ?? []).filter((item) => item !== "pinRowSubMenu")}
				getRowClass={(params) => (params.node.rowPinned === "bottom" ? "font-bold" : undefined)}
				columns={columns}
				isRowSelectable={(row) => !row.isCash}
				onDelete={(ids) => update((latest) => setDeleted(latest, ids, true, aum))}
				onRestore={(ids) => update((latest) => setDeleted(latest, ids, false, aum))}
				onProxy={(proxied) => {
					const byId = new Map(proxied.map((x) => [x.id, x.proxies]));
					update((latest) =>
						applyReviewChanges(
							latest,
							latest.flatMap((row) => {
								const proxies = byId.get(row.id);
								return proxies ? [{ ...row, proxies, hasBeenReviewed: true }] : [];
							}),
							aum,
						),
					);
				}}
				onResolveIssue={resolveIssues}
				onRowsValueChange={(changed) => update((latest) => applyReviewChanges(latest, changed, aum))}
			/>
		</>
	);
}

function isCashRow(row: QuantityReviewRow): boolean {
	return Boolean(row.isCash);
}

function aliasOutcomeMessage(t: TFunction, alias: string, outcome: AliasOutcome): string | undefined {
	switch (outcome.kind) {
		case "failed":
			return t("EDIT_COMPOSITION.REVIEW_QUANTITY.ALIAS_SIZING_FAILED", { alias, message: outcome.message });
		case "quantityOnHeldByValue":
			return t("EDIT_COMPOSITION.REVIEW_QUANTITY.ALIAS_HELD_BY_VALUE", { alias });
		case "answer":
		case "synthetic":
			return undefined;
		default:
			return unreachable(outcome);
	}
}

function blockedSaveMessage(
	t: TFunction,
	check: Exclude<ReviewSaveCheck, { kind: "ok" }>,
	rows: QuantityReviewRow[],
	aum: number,
	currency: Currencies | undefined,
): string {
	switch (check.kind) {
		case "needsReview":
			return "You need to review your composition";
		case "sizingPending":
			return t("EDIT_COMPOSITION.REVIEW_QUANTITY.SIZING_PENDING");
		case "sizingFailed":
			return t("EDIT_COMPOSITION.REVIEW_QUANTITY.SIZING_FAILED");
		case "unsized":
			return t("EDIT_COMPOSITION.REVIEW_QUANTITY.UNSIZED");
		case "unvaluedSynthetic":
			return t("EDIT_COMPOSITION.REVIEW_QUANTITY.UNVALUED_SYNTHETIC");
		case "unpriced":
			return t("EDIT_COMPOSITION.REVIEW_QUANTITY.UNPRICED");
		case "negativeCash":
			return t("EDIT_COMPOSITION.REVIEW_QUANTITY.NEGATIVE_CASH", {
				amount: formatCurrencyAmount(Math.abs(computeCashMarketValue(rows, aum)), currency),
			});
		default:
			return unreachable(check);
	}
}
